import classnames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import React from 'react';
import Modal from 'react-modal';
import useSWR from 'swr';

import { XMarkIcon } from '@heroicons/react/20/solid';

import CommentPopup from 'components/CommentPopup';
import Footer from 'components/Footer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';
import RedeemModal from 'components/RedeemModal';
import ReferralModal from 'components/ReferralModal';

import useRWD from 'hooks/useRWD';
import { getImageUrl } from 'utils';
import api, { swrFetcher } from 'utils/api';
import { MixpanelConfig } from 'utils/mixpanelConfig';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useUserAuth } from 'contexts/UserAuth';

import Breadcrumbs from 'components/Breadcrumbs';
import M2MModal from 'components/M2MModal';
import ProfileNav from 'components/ProfileNav';

const { useEffect, useState } = React;

const PromoCodeCompletedModal = (props) => {
  const { isOpen, setIsOpen } = props;
  const { user } = useUserAuth();
  const email = user ? user.email || '' : '';
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      shouldCloseOnOverlayClick={true}
      className="h-full max-h-none w-full overflow-y-auto rounded-none bg-white px-4 pb-8 pt-3 outline-none lg:h-auto lg:max-h-[80vh] lg:w-[480px] lg:rounded-lg lg:pb-11"
      overlayClassName="z-50 absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
    >
      <div className="flex h-full w-full flex-col">
        <div className="flex items-center justify-between">
          <button
            className="rounded-lg border border-gray-100 p-2.5"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            <XMarkIcon className="size-5 text-[#111928]" />
          </button>

          <div className="w-[48px]" />
        </div>
        <div className="">
          <div className="flex flex-col items-center justify-center pt-[124px]">
            <img
              alt="gift"
              src="/images/complete-gift.png"
              className="h-[128px] w-[128px]"
            />
            <p className="mt-12 text-xl font-bold">已發送兌換碼</p>
            <p className="mt-2 px-5 text-center text-sm text-gray-800">
              已發送相關兌換碼 到您的註冊電郵（{email}）， 請查看郵件。
            </p>
            <button
              className="mt-11 flex w-[311px] items-center justify-center rounded-xl bg-sorra-pink py-3 text-white hover:opacity-70 disabled:opacity-50 lg:mt-[142px]"
              onClick={() => {
                // setIsOpen(false);
                window.location.reload();
              }}
            >
              做任務
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default function Mission() {
  const { isLg } = useRWD();
  const router = useRouter();
  const { pathname, isReady } = router;
  const { isLoading: isAppLaunchLoading, notifications } = useAppLaunch();

  const { user, getAuthorizationHeader } = useUserAuth();

  const postSignupQuestionnaireFilled = _.get(
    user,
    'post_signup_questionnaire_filled',
    false
  );

  const [isReferralModalShow, setIsReferralModalShow] = useState(false);
  const [isCommentModalShow, setIsCommentModalShow] = useState(false);
  const [isRedeemModalShow, setIsRedeemModalShow] = useState(false);
  const [isRedeemPromoCodeModalShow, setIsRedeemPromoCodeModalShow] =
    useState(false);

  const [
    currentSorraUserM2mProgramRewards,
    setCurrentSorraUserM2mProgramRewards,
  ] = useState([]);
  const [currentSorraUserM2mProgram, setCurrentSorraUserM2mProgram] =
    useState(0);

  const [currentCampaignRewards, setCurrentCampaignRewards] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(0);
  const [unpaidRedeem, setUnpaidRedeem] = useState(false);

  const [isRequestingId, setIsRequestingId] = useState(null);

  const [showM2m, setShowM2m] = useState(false);

  useEffect(() => {
    if (isReady && isLg && pathname === '/profile') {
      router.push('/profile/mission');
    }
  }, [isLg, isReady, pathname]);

  // time tick
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const {
    data: m2mProgram,
    isLoading: isM2mLoading,
    mutate: mutateM2mProgram,
  } = useSWR(
    user ? '/api/v3/m2mProgram' : null,
    (url) => swrFetcher(url, getAuthorizationHeader)
    // { fallbackData: [] }
  );

  const {
    data: missions,
    isLoading: isMissionsLoading,
    mutate: mutateMissions,
  } = useSWR(
    user ? '/api/v3/me/missions' : null,
    (url) => swrFetcher(url, getAuthorizationHeader)
    // { fallbackData: [] }
  );

  const isLoading = isAppLaunchLoading || isMissionsLoading;
  const breadcrumbs =
    pathname === '/profile'
      ? [
          { name: '首頁', href: '/' },
          { name: '個人中心', href: null },
        ]
      : [
          { name: '首頁', href: '/' },
          { name: '個人中心', href: '/profile' },
          { name: '任務', href: null },
        ];

  return (
    <div>
      <Menubar
        props={{
          homeExtend: false,
          sourceUI: 'Profile',
        }}
      />
      <div
        className={
          'globals-centerContentsMargin relative' +
          (notifications && notifications.length > 0 ? ' withNoti' : '')
        }
      >
        <MaxWidthContainer className="px-4 lg:px-0">
          <div className="pb-3.5 pt-[22px] lg:pb-10 lg:pt-10">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="flex flex-row gap-x-6 pb-30">
            <div
              className={classnames(
                'w-full flex-shrink-0 lg:block lg:w-[302px]',
                {
                  hidden: pathname === '/profile/mission',
                  block: pathname === '/profile',
                }
              )}
            >
              <ProfileNav />
            </div>
            <div
              className={classnames('min-w-0 grow lg:block', {
                block: pathname === '/profile/mission',
                hidden: pathname === '/profile',
              })}
            >
              <div className="flex flex-col gap-y-8">
                {isLoading ? (
                  <div className="flex h-[800px] flex-col items-center justify-center gap-y-3">
                    <img
                      className="h-[100px] w-[100px]"
                      src="/images/search-placeholder.gif"
                      alt="loading"
                    />
                  </div>
                ) : (
                  <>
                    {user && user.suspicious && (
                      <div className="flex items-center gap-x-2.5 rounded-lg bg-[#FCE1E5] p-4">
                        <div className="shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.5002 8.59688V6.79688C4.5002 5.6034 4.9743 4.45881 5.81821 3.61489C6.66213 2.77098 7.80672 2.29688 9.0002 2.29688C10.1937 2.29688 11.3383 2.77098 12.1822 3.61489C13.0261 4.45881 13.5002 5.6034 13.5002 6.79688V8.59688C13.9776 8.59688 14.4354 8.78652 14.773 9.12408C15.1106 9.46165 15.3002 9.91949 15.3002 10.3969V14.8969C15.3002 15.3743 15.1106 15.8321 14.773 16.1697C14.4354 16.5072 13.9776 16.6969 13.5002 16.6969H4.5002C4.02281 16.6969 3.56497 16.5072 3.2274 16.1697C2.88984 15.8321 2.7002 15.3743 2.7002 14.8969V10.3969C2.7002 9.91949 2.88984 9.46165 3.2274 9.12408C3.56497 8.78652 4.02281 8.59688 4.5002 8.59688V8.59688ZM11.7002 6.79688V8.59688H6.3002V6.79688C6.3002 6.08079 6.58466 5.39404 7.09101 4.88769C7.59736 4.38134 8.28411 4.09688 9.0002 4.09688C9.71628 4.09688 10.403 4.38134 10.9094 4.88769C11.4157 5.39404 11.7002 6.08079 11.7002 6.79688V6.79688Z"
                              fill="#9B1C1C"
                            />
                          </svg>
                        </div>
                        <div className="text-sm text-red-800">
                          <p className="font-medium">帳戶異常通知</p>
                          <p>
                            我們檢測到帳戶有不規則活動，正在審核中。你仍可正常使用平台，但積分新增需經人手審核，可能稍有延遲。
                          </p>
                          <p>
                            請留意電郵（{user.email}
                            ），我們可能需要你協助核實部分活動。如有疑問，歡迎聯絡我們：cs@sorra.net。
                          </p>
                        </div>
                      </div>
                    )}

                    {m2mProgram &&
                      (m2mProgram.banner || m2mProgram.mobile_banner) && (
                        <button
                          className="aspect-[343/193] overflow-hidden rounded-[6px] bg-sorra-gray-50 lg:aspect-[954/193]"
                          onClick={() => {
                            setShowM2m(true);
                          }}
                        >
                          <img
                            src={getImageUrl(m2mProgram.banner)}
                            className="hidden h-full w-full object-cover lg:block"
                          />
                          <img
                            src={getImageUrl(m2mProgram.mobile_banner)}
                            className="block h-full w-full object-cover lg:hidden"
                          />
                        </button>
                      )}

                    {missions && missions.length > 0
                      ? missions.map((missionGroup, idx) => {
                          const { category, missions: tasks } = missionGroup;
                          return (
                            <div key={idx}>
                              <p className="mb-3 text-lg font-medium text-[#111928]">
                                {category}
                              </p>
                              <div className="flex flex-col gap-y-3">
                                {tasks.map((t, idx) => {
                                  let unpaid = false;
                                  let diffFormatted = null;
                                  if (
                                    t.action === 'campaign_redeem' ||
                                    t.action === 'm2m_redeem'
                                  ) {
                                    unpaid =
                                      _.get(t, 'redeem.stripe_session') &&
                                      _.get(t, 'redeem.stripe_status') ===
                                        'unpaid';
                                    // time diff in mm:ss
                                    const redeemExpireTime = _.get(
                                      t,
                                      'redeem.stripe_expires_at'
                                    );
                                    const countdownTime = redeemExpireTime
                                      ? new Date(redeemExpireTime)
                                      : null;
                                    const timeDiff = countdownTime
                                      ? DateTime.fromJSDate(countdownTime).diff(
                                          DateTime.fromJSDate(time)
                                        )
                                      : null;
                                    const diffSeconds = timeDiff
                                      ? timeDiff.as('seconds')
                                      : 0;
                                    diffFormatted =
                                      diffSeconds > 0
                                        ? `(${timeDiff.toFormat('mm:ss')})`
                                        : '';
                                  }

                                  return (
                                    <div
                                      key={idx}
                                      className="flex rounded-2xl bg-[#F5F5F5] p-6"
                                    >
                                      <div>
                                        <div className="text-base font-bold text-sorra-gray-900 lg:text-xl">
                                          {t.name}
                                        </div>
                                        <div className="mt-1 hidden items-center text-xs font-medium text-sorra-gray-900 lg:flex lg:text-base">
                                          {t.score != null ? (
                                            <>
                                              {t.prefix} +
                                              <img
                                                src="/svgs/sorra-points-small.svg"
                                                className="mx-[4px] h-[16px] w-[16px]"
                                              ></img>
                                              ${t.score}
                                              {t.suffix}
                                            </>
                                          ) : (
                                            <>
                                              {t.prefix}&nbsp;
                                              <a
                                                href={t.link}
                                                target="_blank"
                                                className="text-sorra-pink underline"
                                              >
                                                {t.linkText}
                                              </a>
                                            </>
                                          )}
                                        </div>
                                        <div className="mt-[4px] flex items-center text-xs font-medium text-sorra-gray-900 lg:hidden lg:text-base">
                                          {t.score != null ? (
                                            <>
                                              {t.prefix} +
                                              <img
                                                src="/svgs/sorra-points-small.svg"
                                                className="mx-[4px] h-[16px] w-[16px]"
                                              ></img>
                                              ${t.score}
                                              {t.suffix}
                                            </>
                                          ) : (
                                            <span className="block lg:hidden">
                                              {t.prefix}&nbsp;
                                              <a
                                                href={t.link}
                                                target="_blank"
                                                className="text-sorra-pink underline"
                                              >
                                                {t.linkText}
                                              </a>
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="ml-auto mr-0 flex min-w-[87px] flex-shrink-0 items-center justify-center lg:min-w-[110px]">
                                        {t.action === 'review' ? (
                                          !postSignupQuestionnaireFilled ? (
                                            <button
                                              className="flex h-[34px] w-full items-center justify-center gap-x-1 rounded-lg bg-[#E7E7E7] px-3 py-2 text-white"
                                              onClick={() => {
                                                setIsCommentModalShow(true);
                                              }}
                                              disabled={true}
                                            >
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 15 15"
                                                fill="none"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  clipRule="evenodd"
                                                  d="M3.75 6.75V5.25C3.75 4.25544 4.14509 3.30161 4.84835 2.59835C5.55161 1.89509 6.50544 1.5 7.5 1.5C8.49456 1.5 9.44839 1.89509 10.1517 2.59835C10.8549 3.30161 11.25 4.25544 11.25 5.25V6.75C11.6478 6.75 12.0294 6.90804 12.3107 7.18934C12.592 7.47064 12.75 7.85218 12.75 8.25V12C12.75 12.3978 12.592 12.7794 12.3107 13.0607C12.0294 13.342 11.6478 13.5 11.25 13.5H3.75C3.35218 13.5 2.97064 13.342 2.68934 13.0607C2.40804 12.7794 2.25 12.3978 2.25 12V8.25C2.25 7.85218 2.40804 7.47064 2.68934 7.18934C2.97064 6.90804 3.35218 6.75 3.75 6.75ZM9.75 5.25V6.75H5.25V5.25C5.25 4.65326 5.48705 4.08097 5.90901 3.65901C6.33097 3.23705 6.90326 3 7.5 3C8.09674 3 8.66903 3.23705 9.09099 3.65901C9.51295 4.08097 9.75 4.65326 9.75 5.25Z"
                                                  fill="white"
                                                />
                                              </svg>
                                              <p className="text-xs lg:text-sm">
                                                待解鎖
                                              </p>
                                            </button>
                                          ) : !t.completed ? (
                                            <button
                                              className={
                                                'flex h-[34px] w-full items-center justify-center rounded-lg px-3 py-2' +
                                                (t.completed == false
                                                  ? ' bg-sorra-pink text-white'
                                                  : ' bg-gray-100 text-gray-500')
                                              }
                                              onClick={() => {
                                                setIsCommentModalShow(true);
                                              }}
                                            >
                                              <p className="text-xs lg:text-sm">
                                                {t.achieved > -1 &&
                                                t.target > -1
                                                  ? '做任務(' +
                                                    t.achieved +
                                                    '/' +
                                                    t.target +
                                                    ')'
                                                  : '做任務'}
                                              </p>
                                            </button>
                                          ) : (
                                            <div className="text-right text-xs font-medium text-sorra-gray-800">
                                              本週已完成{t.target}篇，
                                              <br />
                                              {t.duration}天後再來！
                                            </div>
                                          )
                                        ) : t.action === 'open_url' ? (
                                          <button
                                            className={
                                              'flex h-[34px] w-full items-center justify-center rounded-[8px] px-[12px] py-[8px]' +
                                              (t.completed == false
                                                ? ' bg-sorra-pink text-white'
                                                : ' bg-gray-100 text-gray-500')
                                            }
                                            onClick={() => {
                                              if (t.completed == false) {
                                                window.open(t.url, '_blank');
                                              }
                                            }}
                                          >
                                            <p className="text-xs lg:text-sm">
                                              {t.completed == false
                                                ? '做任務'
                                                : '已完成'}
                                            </p>
                                          </button>
                                        ) : t.action === 'campaign_redeem' ? (
                                          <button
                                            className={
                                              'flex h-[34px] w-full items-center justify-center rounded-[8px] px-[12px] py-[8px] disabled:opacity-50' +
                                              (t.completed == false || unpaid
                                                ? ' bg-sorra-pink text-white'
                                                : ' bg-gray-100 text-gray-500') +
                                              (t.completed == true && !unpaid
                                                ? ' cursor-default'
                                                : '')
                                            }
                                            disabled={
                                              isRequestingId ===
                                              `campaign_${t.campaign}`
                                            }
                                            onClick={async () => {
                                              if (t.type === 'code') {
                                                if (!t.completed) {
                                                  const campaignIdentifier = `campaign_${t.campaign}`;

                                                  setIsRequestingId(
                                                    campaignIdentifier
                                                  );
                                                  try {
                                                    const headers =
                                                      await getAuthorizationHeader();
                                                    const { campaign } = t;
                                                    const { data } =
                                                      await api.request({
                                                        method: 'POST',
                                                        headers,
                                                        url: `/api/v3/me/redeemCampaignPromoCode`,
                                                        data: { campaign },
                                                      });
                                                    setIsRedeemPromoCodeModalShow(
                                                      true
                                                    );
                                                  } catch (error) {
                                                  } finally {
                                                    setIsRequestingId(null);
                                                  }
                                                }
                                              } else {
                                                if (!t.completed || unpaid) {
                                                  const {
                                                    campaign,
                                                    redeem = null,
                                                  } = t;
                                                  const shippingFee = true;
                                                  if (shippingFee && !redeem) {
                                                    router.push(
                                                      `/profile/welcomegift/${campaign}`
                                                    );
                                                    return;
                                                  }

                                                  const headers =
                                                    await getAuthorizationHeader();
                                                  try {
                                                    const { data } =
                                                      await api.request({
                                                        method: 'GET',
                                                        url: `/api/v2/campaignRewards`,
                                                        params: { campaign },
                                                        headers,
                                                      });
                                                    setCurrentCampaign(
                                                      campaign
                                                    );
                                                    setCurrentCampaignRewards(
                                                      data
                                                    );
                                                    setCurrentSorraUserM2mProgram(
                                                      null
                                                    );
                                                    setCurrentSorraUserM2mProgramRewards(
                                                      []
                                                    );
                                                    setIsRedeemModalShow(true);
                                                    setUnpaidRedeem(redeem);
                                                  } catch (error) {}
                                                }
                                              }
                                            }}
                                          >
                                            <p className="text-xs lg:text-sm">
                                              {t.completed == false
                                                ? '去換領'
                                                : unpaid
                                                  ? `去付款 ${diffFormatted}`
                                                  : '已換領'}
                                            </p>
                                          </button>
                                        ) : t.action === 'm2m_redeem' ? (
                                          <button
                                            className={
                                              'flex h-[34px] w-full items-center justify-center rounded-[8px] px-[12px] py-[8px] disabled:opacity-50' +
                                              (t.completed == false || unpaid
                                                ? ' bg-sorra-pink text-white'
                                                : ' bg-gray-100 text-gray-500') +
                                              (t.completed == true && !unpaid
                                                ? ' cursor-default'
                                                : '')
                                            }
                                            disabled={
                                              isRequestingId ===
                                              `m2m_${t.sorraUserM2mProgram}`
                                            }
                                            onClick={async () => {
                                              if (!t.completed || unpaid) {
                                                const {
                                                  sorraUserM2mProgram,
                                                  redeem = null,
                                                } = t;
                                                const shippingFee = true;
                                                if (shippingFee && !redeem) {
                                                  router.push(
                                                    `/profile/m2mgift/${sorraUserM2mProgram}`
                                                  );
                                                  return;
                                                }
                                                const headers =
                                                  await getAuthorizationHeader();
                                                try {
                                                  const { data } =
                                                    await api.request({
                                                      method: 'GET',
                                                      url: `/api/v3/m2mRewards`,
                                                      params: {
                                                        programId:
                                                          sorraUserM2mProgram,
                                                      },
                                                      headers,
                                                    });
                                                  setCurrentCampaign(null);
                                                  setCurrentCampaignRewards([]);
                                                  setCurrentSorraUserM2mProgram(
                                                    sorraUserM2mProgram
                                                  );
                                                  setCurrentSorraUserM2mProgramRewards(
                                                    data
                                                  );
                                                  setIsRedeemModalShow(true);
                                                  setUnpaidRedeem(redeem);
                                                } catch (error) {}
                                              }
                                            }}
                                          >
                                            <p className="text-xs lg:text-sm">
                                              {t.completed == false
                                                ? '去換領'
                                                : unpaid
                                                  ? `去付款 ${diffFormatted}`
                                                  : '已換領'}
                                            </p>
                                          </button>
                                        ) : (
                                          <button
                                            className={
                                              'flex h-[34px] w-full items-center justify-center rounded-[8px] px-[12px] py-[8px]' +
                                              (t.completed == false
                                                ? ' bg-sorra-pink text-white'
                                                : ' bg-gray-100 text-gray-500')
                                            }
                                            onClick={() => {
                                              if (t.completed == false) {
                                                switch (t.action) {
                                                  case 'referral':
                                                    setIsReferralModalShow(
                                                      true
                                                    );
                                                    break;
                                                  case 'post_signup_questionnaire':
                                                    // MixpanelConfig.track(
                                                    //   'profile_started'
                                                    // );
                                                    window.location =
                                                      '/question';
                                                    break;
                                                  case 'advanced_questionnaire':
                                                    MixpanelConfig.track(
                                                      'advprofile_started'
                                                    );
                                                    window.location =
                                                      '/advance1';
                                                    break;
                                                  default:
                                                    break;
                                                }
                                              }
                                            }}
                                          >
                                            <p className="text-xs lg:text-sm">
                                              {t.completed == false
                                                ? '做任務'
                                                : '已完成'}
                                            </p>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })
                      : missions &&
                        missions.length === 0 && (
                          <div className="flex flex-col items-center justify-center pt-30 lg:pt-40">
                            <img
                              src="/images/icons/sorra-empty.png"
                              className="h-32 w-32 object-contain lg:h-40 lg:w-40"
                            />
                            <p className="mt-2 text-lg font-medium">暫無任務</p>
                          </div>
                        )}
                  </>
                )}
              </div>
            </div>
          </div>
        </MaxWidthContainer>
      </div>

      {isRedeemModalShow && (
        <RedeemModal
          isShow={isRedeemModalShow}
          setIsShow={setIsRedeemModalShow}
          currentCampaignRewards={currentCampaignRewards}
          currentCampaign={currentCampaign}
          currentSorraUserM2mProgramRewards={currentSorraUserM2mProgramRewards}
          currentSorraUserM2mProgram={currentSorraUserM2mProgram}
          unpaidRedeem={unpaidRedeem}
          mutateMissions={mutateMissions}
        />
      )}

      {isCommentModalShow && (
        <CommentPopup
          externalPopupShown={isCommentModalShow}
          externalProductID={null}
          externalProductBrandName={null}
          externalProductName={null}
          setExternalPopupShown={setIsCommentModalShow}
        />
      )}

      {user && isReferralModalShow && (
        <ReferralModal
          isShow={isReferralModalShow}
          setIsShow={setIsReferralModalShow}
          profileReferralCode={user.referral_code}
          profileReferralCount={user.referral_count}
        />
      )}

      {m2mProgram && (
        <M2MModal
          show={showM2m}
          setShow={setShowM2m}
          program={m2mProgram}
          mutate={mutateM2mProgram}
        />
      )}

      <Footer />
      <MobileTabBar props={{ highlighted: 'account' }} />
      <PromoCodeCompletedModal
        isOpen={isRedeemPromoCodeModalShow}
        setIsOpen={setIsRedeemPromoCodeModalShow}
      />
    </div>
  );
}
